import type { Me, MeClients } from '@/services/types';
import { errorBodyMessage } from '@/utils/nestedErrorMessage';

import type { AuthClient, AuthSession, EmqxToken, LoginEmailCheck, LoginOptions } from './types';

/** `Me` as the mapper accepts it: a membership without `locationIds` gets an empty list. */
type MeInput = Omit<Me, 'clients'> & {
    clients: Array<Omit<MeClients, 'locationIds'> & { locationIds?: number[] }>;
};

export function toAuthSession(me: MeInput): AuthSession {
    return {
        user: me.user ?? null,
        clients:
            me.clients?.map(
                (c): AuthClient => ({
                    role: c.role,
                    authLicenses: c.authLicenses,
                    locationIds: c.locationIds ?? [],
                    id: c.client.id,
                    name: c.client.name,
                    locations: c.client.locations,
                    archived: c.client.archived === true,
                })
            ) ?? [],
    };
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

function isLoginOptions(body: unknown): body is LoginOptions {
    return isRecord(body) && Array.isArray(body.authProviders);
}

/** Reads a check-email reply by status, the way the login screen reports it. */
export function toLoginEmailCheck(status: number, ok: boolean, body: unknown): LoginEmailCheck {
    if (ok) {
        if (!isLoginOptions(body)) throw new Error('Unexpected check-email response');
        return { outcome: 'ok', options: body };
    }
    if (status === 400) {
        return { outcome: 'invalid', message: errorBodyMessage(body) ?? null };
    }
    if (status === 404) return { outcome: 'unknownDomain' };
    return { outcome: 'failed' };
}

/** The token endpoint is untyped in the API spec, so its body is checked here. */
export function toEmqxToken(body: unknown): EmqxToken {
    if (!isRecord(body) || typeof body.token !== 'string') {
        throw new Error('EMQX token response has no token');
    }
    return {
        token: body.token,
        username: typeof body.username === 'string' && body.username ? body.username : null,
    };
}
